import React, { useRef, useState } from "react";
import { FileText, FileUp, Download, Loader2, ScanSearch, Trash2, ListChecks } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import api from "@/lib/api";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useLang, translateFr as tfr } from "@/i18n";
import { negCard, DOC_KIND_FR, fmtSize } from "./negotiationShared";
import { ExtractionReview } from "./ExtractionReview";

const VTYPE_FR = { offer: tfr("Offre initiale"), counter_offer: tfr("Contre-offre"), revised_offer: tfr("Offre révisée"), final_agreement: tfr("Accord final") };
const KIND_TONE = {
  offer: "text-sky-500 bg-sky-500/10 border-sky-500/25",
  counter_offer: "text-amber-500 bg-amber-500/10 border-amber-500/25",
  final_contract: "text-red-500 bg-red-500/10 border-red-500/25",
  annex: "text-purple-500 bg-purple-500/10 border-purple-500/25",
  other: "text-muted-foreground bg-secondary/50 border-border dark:border-border",
};

export function NegotiationDocs({ neg, versions, documents, extractions, comparison, onChanged }) {
  const { tf, locale } = useLang();
  const inputRef = useRef(null);
  const [vid, setVid] = useState("");
  const [kind, setKind] = useState("offer");
  const [busyId, setBusyId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [reviewDoc, setReviewDoc] = useState(null);

  const sorted = [...versions].sort((a, b) => b.version_number - a.version_number);
  const draft = versions.find((v) => v.status === "draft");
  const selVid = vid || draft?.id || sorted[0]?.id || "";
  const byVersion = {};
  documents.forEach((d) => { (byVersion[d.negotiation_version_id] = byVersion[d.negotiation_version_id] || []).push(d); });

  const upload = async (file) => {
    if (!file || !selVid) return;
    if (file.size > 15 * 1024 * 1024) { toast.error(tf("Fichier trop volumineux (max 15 Mo)")); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("doc_kind", kind);
      await api.post(`/negotiations/${neg.id}/versions/${selVid}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(tf("Document joint à la version"));
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || tf("Échec de l'envoi")); }
    finally { setUploading(false); if (inputRef.current) inputRef.current.value = ""; }
  };

  const extract = async (doc) => {
    setBusyId(doc.id);
    try {
      const r = await api.post(`/negotiations/${neg.id}/documents/${doc.id}/extract`);
      const c = r.data.counts;
      toast.success(`${tf("Document analysé")} — ${c.detected} ${tf("termes détectés")}`);
      onChanged?.();
      setReviewDoc(doc.id);
    } catch (e) { toast.error(e?.response?.data?.detail || tf("Erreur")); }
    finally { setBusyId(null); }
  };

  const remove = async (doc) => {
    setBusyId(doc.id);
    try {
      await api.delete(`/negotiations/${neg.id}/documents/${doc.id}`);
      toast.success(tf("Document retiré"));
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || tf("Erreur")); }
    finally { setBusyId(null); }
  };

  const download = (doc) => {
    const base = process.env.REACT_APP_BACKEND_URL;
    window.open(`${base}/api/contracts/${neg.contract_id}/documents/${doc.id}/download`, "_blank");
  };

  const extByDoc = {};
  extractions.forEach((x) => { (extByDoc[x.document_id] = extByDoc[x.document_id] || []).push(x); });

  return (
    <div className={cn(negCard, "p-4")} data-testid="neg-docs">
      <h3 className="text-[11px] uppercase tracking-[0.16em] font-semibold text-foreground/85 mb-3">{tf("Documents de négociation")}</h3>

      {versions.length === 0 ? (
        <p className="text-sm text-muted-foreground">{tf("Créez d'abord une version pour y joindre des documents.")}</p>
      ) : (
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <Select value={selVid} onValueChange={setVid}>
            <SelectTrigger className="h-9 w-auto min-w-[130px]" data-testid="neg-doc-version-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {sorted.map((v) => (
                <SelectItem key={v.id} value={v.id}>V{v.version_number} · {tf(VTYPE_FR[v.version_type])}{v.status === "draft" ? ` (${tf("brouillon")})` : ""}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={kind} onValueChange={setKind}>
            <SelectTrigger className="h-9 w-auto min-w-[120px]" data-testid="neg-doc-kind-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(DOC_KIND_FR).map(([k, l]) => <SelectItem key={k} value={k}>{tf(l)}</SelectItem>)}
            </SelectContent>
          </Select>
          <input ref={inputRef} type="file" className="hidden" accept=".pdf,.txt,.csv,.md,application/pdf,text/plain"
            onChange={(e) => upload(e.target.files?.[0])} data-testid="neg-doc-file-input" />
          <Button variant="outline" size="sm" className="h-9" disabled={uploading || !selVid} data-testid="neg-doc-upload-btn"
            onClick={() => inputRef.current?.click()}>
            {uploading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <FileUp className="h-3.5 w-3.5 mr-1.5" />}
            {tf("Joindre un document")}
          </Button>
        </div>
      )}

      {documents.length === 0 ? (
        <p className="text-xs text-muted-foreground" data-testid="neg-docs-empty">{tf("Aucun document joint. Joignez une offre ou un contrat pour lancer l'analyse.")}</p>
      ) : (
        <div className="space-y-3">
          {sorted.filter((v) => byVersion[v.id]?.length).map((v) => (
            <div key={v.id} data-testid={`neg-docs-version-${v.version_number}`}>
              <div className="text-[11px] font-semibold text-muted-foreground mb-1.5">V{v.version_number} · {tf(VTYPE_FR[v.version_type])}</div>
              <ul className="space-y-1.5">
                {byVersion[v.id].map((d) => {
                  const exts = extByDoc[d.id] || [];
                  const pending = exts.filter((x) => ["pending_review", "needs_review"].includes(x.status)).length;
                  const validated = exts.filter((x) => ["accepted", "modified_and_accepted"].includes(x.status)).length;
                  return (
                    <li key={d.id} className="flex flex-wrap items-center gap-2 rounded-xl border border-border/60 dark:border-border px-3 py-2" data-testid={`neg-doc-${d.id}`}>
                      <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium truncate">{d.filename}</div>
                        <div className="text-[11px] text-muted-foreground">{new Date(d.uploaded_at).toLocaleDateString(locale)} · {fmtSize(d.size)}</div>
                      </div>
                      <span className={cn("text-[10px] px-2 py-0.5 rounded-full border shrink-0", KIND_TONE[d.doc_kind] || KIND_TONE.other)}>{tf(DOC_KIND_FR[d.doc_kind] || d.doc_kind)}</span>
                      {exts.length > 0 && (
                        <button type="button" onClick={() => setReviewDoc(d.id)} data-testid={`neg-doc-review-${d.id}`}
                          className="text-[11px] px-2 py-0.5 rounded-full border border-amber-500/25 text-amber-500 bg-amber-500/10 hover:bg-amber-500/20 inline-flex items-center gap-1">
                          <ListChecks className="h-3 w-3" />
                          {pending > 0 ? `${pending} ${tf("à vérifier")}` : `${validated} ${tf("validés")}`}
                        </button>
                      )}
                      <div className="flex items-center gap-1 shrink-0">
                        <Button variant="ghost" size="sm" className="h-8 px-2" onClick={() => download(d)} aria-label={tf("Télécharger")} data-testid={`neg-doc-download-${d.id}`}><Download className="h-3.5 w-3.5" /></Button>
                        <Button variant="ghost" size="sm" className="h-8 px-2" disabled={busyId === d.id} onClick={() => extract(d)} aria-label={tf("Analyser le document")} data-testid={`neg-doc-extract-${d.id}`}>
                          {busyId === d.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ScanSearch className="h-3.5 w-3.5" />}
                        </Button>
                        {v.status === "draft" && (
                          <Button variant="ghost" size="sm" className="h-8 px-2 text-muted-foreground hover:text-red-500" disabled={busyId === d.id} onClick={() => remove(d)} aria-label={tf("Supprimer")} data-testid={`neg-doc-delete-${d.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      )}
      <p className="text-[10px] text-muted-foreground mt-3">{tf("Les valeurs extraites par Athlynx Intelligence restent des suggestions tant que vous ne les validez pas.")}</p>

      <ExtractionReview open={!!reviewDoc} onOpenChange={(o) => !o && setReviewDoc(null)}
        neg={neg} documentId={reviewDoc} documents={documents} extractions={extractions}
        comparison={comparison} draft={draft} onChanged={onChanged} />
    </div>
  );
}
